//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { Button, IconButton, Modal, Accordion as MuiAccordion, AccordionDetails as MuiAccordionDetails, AccordionSummary as MuiAccordionSummary, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Box, Paper, Divider, Chip, Tooltip } from '@mui/material';

import React, { useState } from 'react';
import styled from 'styled-components';
//  -- Components ---------------------
import { Col, Row } from 'antd';
import Loading from 'components/Loading';
//  -- Constants ----------------------
import { get, isEmpty, map } from 'lodash';
//  -- Thunks -------------------------
import { ArrowForward, ArrowRight, Close, Person, School, Class, Group, Fingerprint, CalendarToday, Badge, VpnKey, Business, LocationOn, IntegrationInstructions, ContentCopy } from '@mui/icons-material';
import PasswordRevealInline from 'components/PasswordRevealInline';
import moment from 'moment';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { createKeystore, openAutologin } from 'store/keystore/thunks';
import { getUserIntegration } from 'utils';
import { COLOR } from 'utils/constants/color';
import { v4 } from 'uuid';
import TeacherSupportForm from './TeacherSupportFrom';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';

const Label = styled.div`
   font-size: 14px;
   color: #000000;
   margin: 8px 0;
   display: flex;
   align-items: center;
   gap: 8px;
`;

const LabelTop = styled.div`
   font-size: 20px;
   font-weight: 600;
   color: ${COLOR.white};
   margin-bottom: 16px;
   text-align: center;
`;

const ItemsTabsList = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  min-width: 300px;
  background: linear-gradient(135deg, ${COLOR.indigodeep} 0%, #1e3a8a 100%);
  width: 100%;
  padding: 24px;
  margin: 16px 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ClassUsersWrapper = styled.div`
    border: 1px solid ${COLOR.black700};
    border-radius: 8px;
    margin: 16px 0;
    width: 100%;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    &:last-child {
        margin-bottom: 0px;
    }
`;

const ModalContent = styled.div`
    align-items: center;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    left: 50%;
    width: 95vw;
    max-width: 1200px;
    height: 90vh;
    padding: 24px;
    position: absolute;
    text-align: center;
    top: 50%;
    transform: translate(-50%, -50%);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ModalSection = styled.div`
    align-items: center;
    background: transparent;
    display: flex;
    flex-direction: column;
    left: 50%;
    width: 100%;
    height: 100%;
    overflow: auto;
    text-align: center;
    padding: 0 8px;
    
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.1);
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.3);
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.5);
    }
`;

const AccordianTitle = styled(Typography)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    font-weight: 500;
`;

// Enhanced Accordion styling
const Accordion = styled(MuiAccordion)(({ theme }) => ({
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    marginBottom: '16px',
    overflow: 'hidden',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '16px 0',
    },
    '&:hover': {
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-2px)',
      transition: 'all 0.3s ease',
    },
  }));
  
  // Enhanced AccordionSummary
  const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
    background: 'linear-gradient(135deg, #03989e 0%, #027a7f 100%)',
    color: 'white',
    textTransform: 'capitalize',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    marginBottom: -1,
    minHeight: 56,
    padding: '0 20px',
    '&.Mui-expanded': {
      minHeight: 56,
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: '12px 0',
    },
    '&:hover': {
      background: 'linear-gradient(135deg, #02a8ae 0%, #03989e 100%)',
    },
  }));
  
  // Enhanced AccordionDetails
  const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: '20px',
    backgroundColor: '#f8fafc',
  }));
  
  // Enhanced Rotate animation
  const Rotate = styled('div')(({ open }) => ({
    alignItems: 'center',
    display: 'flex',
    height: '20px',
    justifyContent: 'center',
    marginRight: '8px',
    transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    width: '20px',
    color: 'white',
  }));

const ContentArea = styled.div`
width: 100%;
margin-top: 24px;
`;

const StyledButton = styled(Button)`
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: white;
    border-radius: 12px;
    padding: 12px 24px;
    font-weight: 600;
    text-transform: none;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    margin-top: 16px;
    transition: all 0.3s ease;
    
    &:hover {
        background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
        box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
        transform: translateY(-2px);
    }
    
    &:disabled {
        background: #9ca3af;
        box-shadow: none;
        transform: none;
    }
`;

const CloseButton = styled(IconButton)`
    position: absolute;
    top: 16px;
    right: 16px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    z-index: 10;
    
    &:hover {
        background: rgba(255, 255, 255, 1);
        transform: scale(1.1);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    }
`;

const InfoCard = styled(Paper)`
    padding: 16px;
    margin: 8px 0;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
`;

const UserInfoAres = ({ data }) => {
    const showAlert = useAlert();
    const [loader, setLoader] = useState(false);
    const [autologinLoader, setAutologinLoader] = useState(false);
    const [showTeacherSupportForm, setShowTeacherSupportForm] = useState(false);
    const [copied, setCopied] = useState(false);

    const openLoginInNewWindow = async (userId) => {
        setLoader(true);
        const key = v4()
        await createKeystore(get(data, "users.id", ''), key)
        const url = `${window.location.origin}?key=${key}`
        window.open(url, '_blank', 'toolbar=0,location=0,menubar=0');
        setLoader(false);
    }

  const handleOpenAutologin = async () => {
        setAutologinLoader(true);
        try {
            const schoologyid = get(data, "users.schoologyid", '');
            const result = await openAutologin(schoologyid);
            if (result && !result.success) {
                showAlert(result.message || 'Autologin URL not found', 'error');
            }
        } catch (error) {
            showAlert('Error fetching autologin URL. Please try again.', 'error');
        } finally {
            setAutologinLoader(false);
        }
    }

    const openTeacherSupportForm = () => {
        setShowTeacherSupportForm(true);
      }
    
    const copyUserInfo = async () => {
        const userInfo = [
            'User on OWE Platform',
            '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
            `Id: ${get(data, "users.id", '')}`,
            `Created: ${get(data, "users.createdAt", null) ? moment(parseInt(get(data, "users.createdAt", ''))).format("MM/DD/YYYY") : 'N/A'}`,
            `Name: ${get(data, "users.firstName", '')} ${get(data, "users.lastName", '')}`,
            `Role: ${get(data, "users.role", '')}`,
            `Username: ${get(data, "users.username", '')}`,
            `Password: (use Show password in UI — not copied)`,
            `School: ${get(data, "users.school", '') || '-'}`,
            `District: ${get(data, "users.district", '') || '-'}`,
            `Classes: ${get(data, "classes.length", 0)}`,
            `Integration: ${getUserIntegration(data?.users) || '-'}`
        ].join('\n');
        
        try {
            await navigator.clipboard.writeText(userInfo);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    return (<Row style={{width:"100%"}}>
        <Col span={24}>
            {!isEmpty(data.users) && <ItemsTabsList>
                <LabelTop style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Person style={{ marginRight: '8px', fontSize: '24px', color: '#ffffff' }} />
                        <label>User on OWE Platform</label>
                        <Tooltip title={copied ? "Copied!" : "Copy user information"} arrow placement="top">
                            <IconButton
                                onClick={copyUserInfo}
                                size="small"
                                style={{
                                    color: '#ffffff',
                                    padding: '4px',
                                    marginLeft: '4px'
                                }}
                            >
                                <ContentCopy style={{ fontSize: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </div>
                 {(get(data, "users.role", '') === 'teacher' || get(data, "users.role", '') === 'admin') && (
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Button 
                            color="primary" 
                            variant="contained" 
                            onClick={openTeacherSupportForm}
                            style={{ 
                                background: 'rgba(255, 255, 255, 0.2)', 
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                fontSize: '13px',
                                padding: '6px 16px'
                            }}
                        >
                            Log Report
                        </Button>
                    </div>
                  )}
                </LabelTop>
                
                <InfoCard>
                    <Label><Fingerprint style={{ marginRight: '8px', color: '#6b7280' }} /><strong>ID:</strong> {get(data, "users.id", '')}</Label>
                    <Label><CalendarToday style={{ marginRight: '8px', color: '#6b7280' }} /><strong>Created:</strong> {get(data, "users.createdAt", null) ? moment(parseInt(get(data, "users.createdAt", ''))).format("MM/DD/YYYY") : null}</Label>
                    <Label><Person style={{ marginRight: '8px', color: '#6b7280' }} /><strong>Name:</strong> {get(data, "users.firstName", '')} {get(data, "users.lastName", '')}</Label>
                    <Label><Badge style={{ marginRight: '8px', color: '#6b7280' }} /><strong>Role:</strong> <Chip label={get(data, "users.role", '')} color="primary" size="small" /></Label>
                    <Label><VpnKey style={{ marginRight: '8px', color: '#6b7280' }} /><strong>Username:</strong> {get(data, "users.username", '')}</Label>
                    <Label><VpnKey style={{ marginRight: '8px', color: '#6b7280' }} /><strong>Password:</strong> <PasswordRevealInline userId={get(data, "users.id")} username={get(data, "users.username")} /></Label>
                </InfoCard>
                
                <InfoCard>
                    <Label><School style={{ marginRight: '8px', color: '#6b7280' }} /><strong>School:</strong> {get(data, "users.school") || '-'}</Label>
                    <Label><LocationOn style={{ marginRight: '8px', color: '#6b7280' }} /><strong>District:</strong> {get(data, "users.district") || '-'}</Label>
                    <Label><Class style={{ marginRight: '8px', color: '#6b7280' }} /><strong>Classes:</strong> {get(data, "classes.length", 0)}</Label>
                    <Label><IntegrationInstructions style={{ marginRight: '8px', color: '#6b7280' }} /><strong>Integration:</strong> {getUserIntegration(data?.users) || '-'}</Label>
                </InfoCard>

                <StyledButton 
                    color="primary" 
                    variant="contained" 
                    onClick={openLoginInNewWindow}
                    disabled={loader}
                    fullWidth
                >
                    {loader && <Loading size={16} white />}
                    {!loader && 'Login to Account'}
                    <ArrowForward
                        style={{
                            fontSize: "16px",
                            marginLeft: "8px",
                        }}
                    />
                </StyledButton>

                {get(data, "users.schoologyid", null) != null && get(data, "users.schoologyid", '') !== '' && (
                  <StyledButton 
                    color="primary" 
                    variant="contained" 
                    onClick={handleOpenAutologin}
                    disabled={autologinLoader}
                    fullWidth
                   >
                    {autologinLoader && <Loading size={16} white />}
                    {!autologinLoader && 'Login With Auto-Login'}
                    <ArrowForward
                        style={{
                            fontSize: "16px",
                            marginLeft: "8px",
                        }}
                    />
                </StyledButton>
                )}

            </ItemsTabsList>}
        </Col>
        
        {showTeacherSupportForm && (
            <TeacherSupportForm 
                visible={showTeacherSupportForm}
                onClose={() => setShowTeacherSupportForm(false)}
                userData={data?.users || {}}
            />
        )}
    </Row>)
}

const ClassInfoArea = ({
  handleChange,
  data,
  classEpendOwe,
  schoologyData,
  expandedAccordions,
}) => {
  return (
    <Row style={{ width: '100%' }}>
      <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
        <ContentArea>
          <Typography
            variant="h5"
            style={{ marginBottom: '20px', color: '#1f2937', fontWeight: 600 }}
          >
            <Group style={{ marginRight: '8px', verticalAlign: 'middle', color: '#1f2937' }} />
            Class Information
          </Typography>
          {map(data.classes, (cls) => {
            const sectionKey = cls.sectionId || cls.id || cls.displayName;
            return (
              <Accordion
                key={sectionKey}
                square
                expanded={!!expandedAccordions[sectionKey]}
                onChange={handleChange(sectionKey)}
              >
                <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                  <AccordianTitle>
                    <span style={{ fontSize: '16px', fontWeight: 500 }}>
                      {cls.displayName}
                      {cls.archived && (
                        <Chip
                          label="Archived"
                          color="warning"
                          size="small"
                          style={{ marginLeft: '8px' }}
                        />
                      )}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Chip
                        icon={<Group />}
                        label={`${get(cls, 'classUsers.length', 0)} Students`}
                        color="info"
                        size="small"
                      />
                      <Rotate open={!!expandedAccordions[sectionKey]}>
                        <ArrowRight />
                      </Rotate>
                    </div>
                  </AccordianTitle>
                </AccordionSummary>
                <AccordionDetails>
                  <ClassUsersWrapper>
                    <TableContainer>
                      <Table style={{ width: '100%', textAlign: 'center' }}>
                        <TableHead>
                          <TableRow style={{ backgroundColor: '#f1f5f9' }}>
                            <TableCell
                              style={{ textAlign: 'center', fontWeight: 600, color: '#374151' }}
                            >
                              ID
                            </TableCell>
                            <TableCell
                              style={{ textAlign: 'center', fontWeight: 600, color: '#374151' }}
                            >
                              Username
                            </TableCell>
                            <TableCell
                              style={{ textAlign: 'center', fontWeight: 600, color: '#374151' }}
                            >
                              First Name
                            </TableCell>
                            <TableCell
                              style={{ textAlign: 'center', fontWeight: 600, color: '#374151' }}
                            >
                              Last Name
                            </TableCell>
                            <TableCell
                              style={{ textAlign: 'center', fontWeight: 600, color: '#374151' }}
                            >
                              Password
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {map(cls.classUsers, (cuser, index) => {
                            return (
                              <TableRow
                                key={get(cuser, 'id', index)}
                                style={{
                                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                                  '&:hover': { backgroundColor: '#f1f5f9' },
                                }}
                              >
                                <TableCell style={{ textAlign: 'center', color: '#6b7280' }}>
                                  {get(cuser, 'id', '')}
                                </TableCell>
                                <TableCell
                                  style={{ textAlign: 'center', fontWeight: 500, color: '#374151' }}
                                >
                                  {get(cuser, 'username', '')}
                                </TableCell>
                                <TableCell style={{ textAlign: 'center', color: '#6b7280' }}>
                                  {get(cuser, 'firstName', '')}
                                </TableCell>
                                <TableCell style={{ textAlign: 'center', color: '#6b7280' }}>
                                  {get(cuser, 'lastName', '')}
                                </TableCell>
                                <TableCell style={{ textAlign: 'center', color: '#6b7280' }}>
                                  <PasswordRevealInline userId={get(cuser, 'id')} username={get(cuser, 'username')} />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </ClassUsersWrapper>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </ContentArea>
      </Col>
    </Row>
  );
};

const GlobalUserDetails = ({ openModal, setOpenModal, userDetails }) => {
  const [data, setData] = useState({ users: null, classes: [], auth: null });
  const [classEpendOwe, setClassExpendOwe] = useState(-1);
  const [expandedAccordions, setExpandedAccordions] = useState({});


  useEffect(() => {
    setData(userDetails)
  }, [userDetails])

  const handleChange = (sectionId) => (event, isExpanded) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [sectionId]: isExpanded
    }));
  };

  return (
    <Modal
      open={openModal}
      onClose={(e) => setOpenModal(false)}
      style={{ 
        height: '100%', 
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)'
        }
      }}
    >
      <ModalContent>
        <CloseButton onClick={(e) => setOpenModal(false)}>
          <Close color="action" />
        </CloseButton>
        
        <ModalSection>
          <UserInfoAres data={data} />
          <ClassInfoArea
            data={data}
            classEpendOwe={classEpendOwe}
            handleChange={handleChange}
            expandedAccordions={expandedAccordions}
          />
        </ModalSection>
      </ModalContent>
    </Modal>
  );
};


//  ----------------------------------------------------------------------------

//  ----------------------------------------------------------------------------
//  Redux
//  ----------------------------------------------------------------------------
const mapStateToProps = (state) => ({
  districts: state.districts,
  allSchools: state.allSchools,
});

export default connect(mapStateToProps, null)(GlobalUserDetails);
